import React from "react";
import { Button, Col, Divider, Form, Input, Modal, Popconfirm, Row, Space, Table, Tag, Checkbox, Typography } from "antd";
import useRoles from "./useRoles";
import { IRole } from "../../types/interfaces";
import { Shield, Plus, Edit, Trash2 } from "lucide-react";
import {
  getPermissionLabel,
  getPermissionCategory,
  PERMISSION_CATEGORIES,
  PermissionCategory,
} from "../../constants/permissions";
import { PermissionBadgeList } from "../../components/general/permissionBadge/PermissionBadge";

const { Title, Text } = Typography;

// Categorize permissions for the checkbox form
const PERMISSION_FORM_CATEGORIES: { key: PermissionCategory; label: string; icon: string }[] = [
  { key: "maps", label: "Mapas", icon: "🗺️" },
  { key: "newsletters", label: "Boletines", icon: "🌎" },
  { key: "management_roles", label: "Gestionar roles y permisos", icon: "🛡️" },
  { key: "management_actions", label: "Gestión de usuarios – Acciones directas sobre usuarios", icon: "⚡" },
  { key: "management_view", label: "Visualización", icon: "👁️" },
  { key: "subscriptions", label: "Suscripciones (Admin)", icon: "⭐" },
  { key: "purchases", label: "Compras e Historial", icon: "💳" },
  { key: "other", label: "Sistema", icon: "⚙️" },
];

function groupPermissions(permissions: any[]) {
  const groups: Record<string, any[]> = {};
  PERMISSION_FORM_CATEGORIES.forEach((c) => (groups[c.key] = []));

  permissions?.forEach((p: any) => {
    const cat = getPermissionCategory(p.name);
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(p);
  });

  const newsletterOrder = [
    "ui:newsletter:peru_1",     // Fila 1 - Izq
    "ui:newsletter:oman",       // Fila 1 - Der (Omán 1)
    "ui:newsletter:peru_2",     // Fila 2 - Izq (Perú v3)
    "ui:newsletter:oman_2",     // Fila 2 - Der (Omán v3)
    "ui:newsletter:ecuador",    // Fila 3 - Izq (Ecuador 1)
    "ui:newsletter:panama",     // Fila 3 - Der (Panamá 1)
    "ui:newsletter:ecuador_2",  // Fila 4 - Izq (Ecuador v3)
    "ui:newsletter:panama_2",   // Fila 4 - Der (Panamá v3)
    "ui:newsletter",            // Fila 5 - Izq (Acceso a boletines)
  ];

  if (groups["newsletters"]) {
    groups["newsletters"].sort((a, b) => {
      const indexA = newsletterOrder.indexOf(a.name);
      const indexB = newsletterOrder.indexOf(b.name);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }

  return groups;
}

export default function RolesManagement() {
  const {
    roles,
    rolesLoading,
    permissions,
    permissionsLoading,
    roleModal,
    form,
    handleOk,
    handleOpenModal,
    handleEdit,
    handleDelete,
    userData,
  } = useRoles();

  const columns = [
    {
      title: "Nombre del Rol",
      dataIndex: "name",
      key: "name",
      width: 250,
      render: (text: string, record: IRole) => (
        <Space>
          <Shield size={16} color={record.is_system ? "#1890ff" : "#52c41a"} />
          <Text strong>{text}</Text>
          {record.is_system && <Tag color="blue">Sistema</Tag>}
        </Space>
      ),
    },
    {
      title: "Descripción",
      dataIndex: "description",
      key: "description",
      render: (text: string) => <Text type="secondary">{text || "Sin descripción"}</Text>,
    },
    {
      title: "Permisos",
      dataIndex: "permissions",
      key: "permissions",
      width: 300,
      render: (_: any, record: any) => {
        const permSlugs = record.edges?.permissions?.map((p: any) => p.name) || [];
        return <PermissionBadgeList permissions={permSlugs} size="small" maxVisible={3} />;
      },
    },
    {
      title: "Acciones",
      key: "actions",
      width: 120,
      render: (_: any, record: IRole) => {
        // System roles (like admin) can be viewed but not edited/deleted
        if (record.is_system) {
          return <Tag color="default">Protegido</Tag>;
        }

        return (
          <Space>
            <Button
              type="text"
              icon={<Edit size={16} />}
              onClick={() => handleEdit(record)}
              style={{ color: "#1890ff" }}
            />
            <Popconfirm
              title="Eliminar rol"
              description="¿Estás seguro? Los usuarios vinculados perderán este rol."
              onConfirm={() => handleDelete(record.id)}
              okText="Eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="text"
                danger
                icon={<Trash2 size={16} />}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const groupedPermissions = groupPermissions(permissions || []);

  return (
    <div style={{ padding: "20px" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>Gestión de Roles y perfiles</Title>
          <Text type="secondary">Define quién puede hacer qué en el sistema.</Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<Plus size={16} />}
            onClick={handleOpenModal}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              borderRadius: "8px",
              background: "#81BC3D",
              borderColor: "#81BC3D",
            }}
          >
            Crear nuevo rol
          </Button>
        </Col>
      </Row>

      <Table
        dataSource={roles}
        columns={columns}
        loading={rolesLoading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        style={{ borderRadius: "8px" }}
      />

      <Modal
        title={roleModal.editing ? "Editar Rol" : "Crear Nuevo Rol"}
        open={roleModal.isModalOpen}
        onOk={handleOk}
        onCancel={roleModal.closeModal}
        confirmLoading={roleModal.isLoading}
        width={650}
        destroyOnClose
        okText={roleModal.editing ? "Actualizar" : "Crear"}
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item
            name="name"
            label="Nombre del Rol"
            rules={[{ required: true, message: "Por favor ingresa el nombre" }]}
          >
            <Input
              placeholder="Ej: Gestor, Empresa, Empleado"
              style={{ borderRadius: "8px" }}
            />
          </Form.Item>

          <Form.Item name="description" label="Descripción">
            <Input.TextArea
              rows={2}
              placeholder="Describe brevemente el alcance de este rol..."
              style={{ borderRadius: "8px" }}
            />
          </Form.Item>

          <Divider orientation="left">Permisos asignados</Divider>

          <Form.Item
            name="permissions"
            rules={[{ required: true, message: "Selecciona al menos un permiso" }]}
          >
            {permissionsLoading ? (
              <Text>Cargando permisos...</Text>
            ) : (
              <Checkbox.Group style={{ width: "100%" }}>
                {PERMISSION_FORM_CATEGORIES.map((cat) => {
                  const catPerms = groupedPermissions[cat.key];
                  if (!catPerms || catPerms.length === 0) return null;
                  const config = PERMISSION_CATEGORIES[cat.key];

                  const isSubCategory = cat.key.startsWith("management_");

                  return (
                    <React.Fragment key={cat.key}>
                      {cat.key === "management_roles" && (
                        <div style={{ marginBottom: "12px", display: "flex", alignItems: "center", gap: "6px" }}>
                          <span>👥</span>
                          <span style={{ fontSize: "13px", fontWeight: 600, color: "#8c8c8c" }}>Gestión de usuarios</span>
                        </div>
                      )}
                      <div
                        style={{
                          marginBottom: isSubCategory ? "12px" : "16px",
                          marginLeft: isSubCategory ? "24px" : "0px",
                          paddingTop: isSubCategory && cat.key !== "management_roles" ? "8px" : "0px",
                          borderTop: isSubCategory && cat.key !== "management_roles" ? "1px dashed #e8e8e8" : "none",
                        }}
                      >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          marginBottom: "8px",
                        }}
                      >
                        <span>{cat.icon}</span>
                        <span
                          style={{
                            fontSize: isSubCategory ? "12px" : "13px",
                            fontWeight: isSubCategory ? 500 : 600,
                            color: config.color,
                          }}
                        >
                          {cat.label}
                        </span>
                      </div>
                      <Row gutter={[8, 8]} style={{ paddingLeft: "8px" }}>
                        {catPerms.map((p: any) => (
                          <Col span={12} key={p.id}>
                            <Checkbox value={p.name}>
                              <span style={{ fontSize: "13px" }}>
                                {getPermissionLabel(p.name)}
                              </span>
                            </Checkbox>
                          </Col>
                        ))}
                      </Row>
                    </div>
                    </React.Fragment>
                  );
                })}
              </Checkbox.Group>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
